import React, {
  useMemo,
} from 'react';
import styled from 'styled-components';
import 'react-image-lightbox/style.css';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import { Booth, DesignElement } from 'models/cms/booths';
import {
  FETCH_ASSETS_URL,
} from 'appenv';

import { ReactComponent as ColourChangeLeft } from 'assets/booths/923-customization/option3/colourchange-left.svg';
import { ReactComponent as ColourChangeRight } from 'assets/booths/923-customization/option3/colourchange-right.svg';

import { ReactComponent as LightingEffect } from 'assets/booths/923-customization/option3/lighting-effect.svg';
import BusinessPopover from 'pages/booths/components/BusinessPopover';
import BoothContainer from '../components/BoothContainer';
import GenericCMSBoothBanner from '../components/GenericCMSBoothBanner';
import useEmailContactsMemo from '../useEmailContactsMemo';

const BoothBackgroundImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option3/bg.png`;
const CounterImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option3/counter.png`;

const BannerGraphicImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option3/graphic-b-backdrop.png`;

const FlowerImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/flower.png`;

const RepresentativeImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option3/representative.png`;

const BackgroundImage = styled.div`
  height: 110%;
  width: 130%;
  position: absolute;
  left: -150px;
  background: url(${BoothBackgroundImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const OverheadBannerContainer = styled.div`
  position: absolute;
  width: 300px;
  height: 160px;
  left: calc((100% - 272px) / 2);
  top: 3px;
`;

const BackgroundContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 20px;
`;

const BackdropBannerContainer = styled.div`
  height: 383px;
  width: 254px;
  position: absolute;
  bottom: -11px;
  left: 43px;
  z-index: 3;
`;

const BannerGraphic = styled.div`
  background: url(${BannerGraphicImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  position: absolute;
  height: 130px;
  bottom: 306px;
  right: 493px;
  width: 190px;
  z-index: 3;
  pointer-events: none;
`;

const PullUpBannerContainer = styled.div`
  height: 262px;
  width: 140px;
  position: absolute;
  bottom: -30px;
  right: -51px;
  z-index: 3;
`;

const TVBackgroundContainer = styled.div`
  height: 190px;
  top: 211px;
  left: 465px;
  width: 335px;
  position: absolute;
  z-index: 3;
  background: #fff;
`;

const TvBannerContainer = styled.div`
  height: 190px;
  top: 211px;
  left: 465px;
  width: 335px;
  position: absolute;
  z-index: 4;
`;

const CounterBannerContainer = styled.div`
  height: 139px;
  bottom: -57px;
  right: 299px;
  width: 280px;
  position: absolute;
  z-index: 3;
`;

const FlowerContainer = styled.div`
  background: url(${FlowerImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  position: absolute;
  height: 60px;
  bottom: 103px;
  right: 375px;
  width: 65px;
  z-index: 3;
`;

const CounterBackground = styled.div`
  background: url(${CounterImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  position: absolute;
  height: 182px;
  bottom: -59px;
  right: 289px;
  width: 299px;
  z-index: 2;
`;

const BusinessPopoverContainer = styled.div`
  position: absolute;
  top: -20px;
  right: -150px;
  width: 340px;
`;

const BackgroundColorContainer = styled(ColourChangeLeft)`
  height: 531px;
  width: 675px;
  position: absolute;
  left: -225px;
  bottom: -78px;

  .st0 {
    fill: ${({ color }) => color || '#90CE4E'};
  }
`;
const CounterColorContainer = styled(ColourChangeRight)`
  height: 514px;
  width: 600px;
  position: absolute;
  left: 140px;
  bottom: -118px;
  z-index: 3;

  .st0 {
    fill: ${({ color }) => color || '#90CE4E'};
  }
`;

const RepresentativeContainer = styled.div`
  background: url(${RepresentativeImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  position: absolute;
  width: 150px;
  height: 155px;
  right: 430px;
  bottom: 100px;
`;

const LightingEffectContainer = styled(LightingEffect)`
  position: absolute;
  width: 546px;
  top: 162px;
  left: 286px;
  z-index: 1;
  opacity: .3;
  .st0, .st1 {
    fill: ${({ color }) => color || '#90CE4E'};
  }
`;

export interface BannerProps {
  booth: Booth;
  handleClick: (type: string, isCMSElement: boolean) => void;
  isPreview?: boolean;
}

const Banner: React.FC<BannerProps> = ({
  booth,
  handleClick = () => null,
  isPreview = false,
}: BannerProps) => {
  const locale = useSelector(selectLocale);
  const backgroundColor: string = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'background_color')?.color, [booth.designElements]);
  const counterColor: string = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'counter_color')?.color, [booth.designElements]);
  const lightingEffectColor: string = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'lighting_effect_color')?.color, [booth.designElements]);
  const overheadBanner: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'overhead_banner'), [booth.designElements]);
  const backdrop: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'backdrop'), [booth.designElements]);
  const counter: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'counter'), [booth.designElements]);
  const pullUpBanner: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'pull_up_banner'), [booth.designElements]);
  const tv: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode.name === 'tv'), [booth.designElements]);
  const tabs = booth?.rootContentElement?.tabs || [];
  const emailContacts = useEmailContactsMemo(tabs);

  return (
    <BoothContainer needOffset={!isPreview}>
      <BackgroundContainer>
        <BackgroundImage />
      </BackgroundContainer>
      <BackgroundColorContainer color={backgroundColor} />
      <CounterColorContainer color={counterColor} />
      <LightingEffectContainer color={lightingEffectColor} />
      <TVBackgroundContainer />
      <BusinessPopoverContainer>
        <BusinessPopover
          boothId={booth.id}
          boothName={booth.name[locale]}
          emailContacts={emailContacts}
          meetupUrl={booth.meetupEnabled ? booth.appointmentBookingUrl : ''}
          livestreams={booth.livestreams}
          videoCallUrl={booth.videoCallUrl}
        />
      </BusinessPopoverContainer>
      <OverheadBannerContainer onClick={() => handleClick(overheadBanner.name, true)}>
        <GenericCMSBoothBanner
          bannerData={overheadBanner}
          onBannerClick={handleClick as any}
        />
      </OverheadBannerContainer>
      <BackdropBannerContainer onClick={() => handleClick(backdrop.name, true)}>
        <GenericCMSBoothBanner
          bannerData={backdrop}
          onBannerClick={handleClick as any}
        />
      </BackdropBannerContainer>
      <BannerGraphic />
      <PullUpBannerContainer onClick={() => handleClick(pullUpBanner.name, true)}>
        <GenericCMSBoothBanner
          bannerData={pullUpBanner}
          onBannerClick={handleClick as any}
        />
      </PullUpBannerContainer>
      <TvBannerContainer onClick={() => handleClick(tv.name, true)}>
        <GenericCMSBoothBanner
          autoPlayVideo={!isPreview}
          bannerData={tv}
          onBannerClick={handleClick as any}
        />
      </TvBannerContainer>
      <CounterBackground />
      <FlowerContainer />
      <CounterBannerContainer onClick={() => handleClick(counter.name, true)}>
        <GenericCMSBoothBanner
          bannerData={counter}
          onBannerClick={handleClick as any}
        />
      </CounterBannerContainer>
      {!isPreview && (
        <RepresentativeContainer />
      )}
    </BoothContainer>
  );
};

export default Banner;
